/**
 * One borrower, one period: the run page.
 *
 * This is the page the whole monitoring template exists to produce — the
 * ledger for a borrower's quarter, every cell traceable to the page it was
 * read from, the covenant tests against last quarter, the gaps still open, and
 * the lock that freezes the lot and hands it to the next period.
 *
 * Structure worth knowing:
 *  - The tab lives in `?tab=`, so a colleague can be sent straight to the gaps.
 *  - Every write refetches the run rather than patching it client-side: the
 *    server recomputes derived figures and covenant results, and a ledger half
 *    updated by the client is a ledger nobody should trust.
 *  - A refetch holds the previous run on screen and dims it (`stale`) instead
 *    of blanking to a skeleton, because most refetches move one cell.
 *  - `PdfViewerProvider` is mounted here for the same reason the KPI workflow
 *    mounts it: without it, clicking a cited number falls back to a signed URL
 *    in a new tab instead of the in-app viewer at the cited page.
 */

import React, {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageTabs from "../../components/Dashboards/PageTabs";
import ErrorDisplay from "../../components/ErrorDisplay";
import CovenantsCompare from "../../components/Monitoring/CovenantsCompare";
import GapsList from "../../components/Monitoring/GapsList";
import LockDialog from "../../components/Monitoring/LockDialog";
import {
	buildMonitoringContext,
	monitoringChipLabel,
} from "../../components/Monitoring/monitoringChatBridge";
import RunHeader from "../../components/Monitoring/RunHeader";
import RunLedgerGrid from "../../components/Monitoring/RunLedgerGrid";
import RunVersionsList from "../../components/Monitoring/RunVersionsList";
import SourcesList from "../../components/Monitoring/SourcesList";
import { PdfViewerProvider } from "../../context/PdfViewerContext";
import { useToast } from "../../context/ToastContext";
import { useDopplerUriOpener } from "../../hooks/useDopplerUriOpener";
import { useMonitoringRun } from "../../hooks/useMonitoringRun";
import { monitoringService } from "../../services/monitoringService";
import { isMonitoringEnabled } from "../../utils/featureFlags";
import type {
	MonitoringCompare,
	MonitoringFigure,
	MonitoringLockPayload,
	MonitoringRunVersion,
	MonitoringSetFigurePayload,
} from "../../types/monitoring";
import RailFallback from "../../components/Chat/RailFallback";
import { useRailThread } from "../../hooks/useRailThread";
import { RUN_THREAD_PARAM } from "../../utils/chatHref";

/** Loaded only when the rail is opened: the chat page is not a small import. */
const MonitoringAnalystRail = lazy(
	() => import("../../components/Monitoring/MonitoringAnalystRail"),
);

type RunTab = "summary" | "covenants" | "gaps" | "sources" | "history";

const TAB_IDS: RunTab[] = [
	"summary",
	"covenants",
	"gaps",
	"sources",
	"history",
];

const isRunTab = (value: string | null): value is RunTab =>
	value !== null && (TAB_IDS as string[]).includes(value);

/** The message a failure should show. Conflicts are shown verbatim. */
const messageOf = (caught: unknown): string =>
	caught instanceof Error ? caught.message : String(caught);

const MonitoringRunView: React.FC = () => {
	const { runId } = useParams<{ runId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const openUri = useDopplerUriOpener();

	const { run, loading, error, stale, refresh } = useMonitoringRun(runId);

	const tabParam = searchParams.get("tab");
	const tab: RunTab = isRunTab(tabParam) ? tabParam : "summary";
	const setTab = useCallback(
		(next: RunTab) => {
			const params = new URLSearchParams(searchParams);
			params.set("tab", next);
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	// ---------------------------------------------------------------- analyst
	// The rail is behind the module's own flag — there is no second switch for
	// it. The URL names the open thread, so a reload lands back on it and a
	// colleague can be sent the conversation as well as the run.
	const analystEnabled = isMonitoringEnabled();
	const {
		threadId: threadParam,
		open: analystOpen,
		setOpen: setAnalystOpen,
		nameThread,
		close: closeAnalyst,
	} = useRailThread(RUN_THREAD_PARAM);
	const toggleAnalyst = useCallback(
		() => setAnalystOpen((open) => !open),
		[setAnalystOpen],
	);

	// --------------------------------------------------------------- compare
	const [compare, setCompare] = useState<MonitoringCompare | null>(null);
	const hasPrior = Boolean(run?.prior);
	const runPublicId = run?.public_id;
	const runVersion = run?.version;
	useEffect(() => {
		if (!runPublicId || !hasPrior) {
			setCompare(null);
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const next = await monitoringService.getCompare(runPublicId);
				if (!cancelled) setCompare(next);
			} catch {
				// 404 = no locked prior. The table then renders current-only,
				// which is the honest answer, not an error worth a banner.
				if (!cancelled) setCompare(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [runPublicId, hasPrior, runVersion]);

	// -------------------------------------------------------------- versions
	const [versions, setVersions] = useState<MonitoringRunVersion[]>([]);
	const [versionsLoading, setVersionsLoading] = useState(false);
	const [versionsError, setVersionsError] = useState<Error | undefined>(
		undefined,
	);
	const historyOpen = tab === "history";
	useEffect(() => {
		if (!runPublicId || !historyOpen) return;
		let cancelled = false;
		setVersionsLoading(true);
		void (async () => {
			try {
				const next = await monitoringService.listRunVersions(runPublicId);
				if (!cancelled) {
					setVersions(next);
					setVersionsError(undefined);
				}
			} catch (caught) {
				if (!cancelled) {
					setVersionsError(
						caught instanceof Error ? caught : new Error(String(caught)),
					);
				}
			} finally {
				if (!cancelled) setVersionsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [runPublicId, historyOpen, runVersion]);

	// ----------------------------------------------------------------- write
	const handleSetFigure = useCallback(
		async (figureKey: string, payload: MonitoringSetFigurePayload) => {
			if (!runPublicId) return;
			// A refused write REJECTS, so the check stays open with what was
			// typed; a committed write whose refresh fails is still a save.
			try {
				await monitoringService.setFigure(runPublicId, figureKey, payload);
			} catch (caught) {
				showToast(messageOf(caught), "error");
				throw caught;
			}
			try {
				await refresh();
			} catch {
				showToast("Saved. The page could not refresh — reload to see the latest.", "info");
			}
		},
		[runPublicId, refresh, showToast],
	);

	// Locking is always an explicit step here; the board only links to the run (DOP-625).
	const [lockOpen, setLockOpen] = useState(false);
	const [locking, setLocking] = useState(false);
	const handleLock = useCallback(
		async (payload: MonitoringLockPayload) => {
			if (!runPublicId) return;
			setLocking(true);
			try {
				await monitoringService.lockRun(runPublicId, payload);
				showToast("Period locked.", "success");
				setLockOpen(false);
				await refresh();
			} catch (caught) {
				// The dialog stays open: the conflict names something the user can
				// act on, and closing it would take the action away with it.
				showToast(messageOf(caught), "error");
			} finally {
				setLocking(false);
			}
		},
		[runPublicId, refresh, showToast],
	);

	const [downloading, setDownloading] = useState(false);
	const handleDownload = useCallback(async () => {
		if (!runPublicId) return;
		setDownloading(true);
		try {
			const file = await monitoringService.exportRun(runPublicId);
			monitoringService.saveExportFile(file);
		} catch (caught) {
			showToast(messageOf(caught), "error");
		} finally {
			setDownloading(false);
		}
	}, [runPublicId, showToast]);

	// -------------------------------------------------------------- evidence
	const handleOpenSource = useCallback(
		(figure: MonitoringFigure) => {
			const anchor = figure.source?.anchor;
			if (!anchor) return;
			openUri(anchor, { filename: figure.source?.filename ?? undefined });
		},
		[openUri],
	);
	const handleOpenUri = useCallback(
		(href: string, filename: string) => openUri(href, { filename }),
		[openUri],
	);
	const handleOpenGaps = useCallback(() => setTab("gaps"), [setTab]);

	const handleOpenBoard = useCallback(() => {
		if (!run) return;
		// The board is a VIEW of the workflow's Data tab now (DOP-480), so the
		// breadcrumb names both: `?tab=` belongs to the shell, `?view=` to the
		// page. Sending `?tab=periods` would land on the shell's default tab.
		navigate(
			`/workflows/monitoring/${run.template.public_id}?tab=quarter&period=${run.period.public_id}`,
		);
	}, [navigate, run]);

	const breaches = run ? run.covenants.filter((covenant) => covenant.result === "breach").length : 0;

	const tabs = useMemo(
		() => [
			// The ids stay what they were, so links already sent still open the
			// same view; only the words changed (DOP-626).
			{ id: "summary" as RunTab, label: "Figures" },
			{
				id: "covenants" as RunTab,
				label: "Covenants",
				...(breaches > 0 ? { count: breaches, countTone: "attention" as const } : {}),
			},
			{
				id: "gaps" as RunTab,
				label: "Checks",
				...((run?.gaps_open ?? 0) > 0
					? { count: run?.gaps_open ?? 0, countTone: "attention" as const }
					: {}),
			},
			{ id: "sources" as RunTab, label: "Files read", count: run?.sources.length ?? 0 },
			{ id: "history" as RunTab, label: "Versions" },
		],
		[run?.gaps_open, run?.sources.length, breaches],
	);

	if (loading && run === null) {
		return (
			<div className="h-full bg-gray-50 px-6 py-6">
				<div className="h-[420px] animate-pulse rounded-lg border border-gray-200 bg-white" />
			</div>
		);
	}

	if (run === null) {
		return (
			<div className="h-full bg-gray-50">
				<ErrorDisplay
					error={error ?? new Error("This run could not be loaded.")}
					onRetry={() => void refresh()}
				/>
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden bg-gray-50">
			<div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
				<RunHeader
					run={run}
					onOpenBoard={handleOpenBoard}
					onDownload={() => void handleDownload()}
					downloading={downloading}
					onOpenLock={() => setLockOpen(true)}
					onOpenChecks={handleOpenGaps}
					analyst={analystEnabled ? { open: analystOpen, onToggle: toggleAnalyst } : null}
				/>

				<PageTabs
					tabs={tabs}
					activeId={tab}
					onSelect={(id) => setTab(id as RunTab)}
					ariaLabel="Run sections"
				/>

				{/* A reload that failed leaves the PREVIOUS run on screen; saying so
				    is what stops it being read as the current one. */}
				{error && (
					<p role="alert" className="px-6 pt-3 text-sm text-red-600">
						Could not reload this run — these numbers may be out of date.{" "}
						<button
							type="button"
							onClick={() => void refresh()}
							className="underline hover:no-underline"
						>
							Try again
						</button>
					</p>
				)}

				<div
					className={`min-h-0 flex-1 overflow-auto px-6 pb-6 pt-3 ${
						stale ? "pointer-events-none opacity-60" : ""
					}`}
					data-testid="monitoring-run-body"
				>
					{tab === "summary" && (
						<div className="space-y-4">
						<RunLedgerGrid
							run={run}
							onOpenSource={handleOpenSource}
							onOpenGaps={handleOpenGaps}
							// No compare (DOP-465): the ledger's prior column reads
							// each figure's own `prior`, which the run states whether
							// or not an earlier period was ever locked. The compare
							// fetch below is now the covenants tab's business alone.
						/>
						</div>
					)}
					{tab === "covenants" && (
						<CovenantsCompare run={run} compare={compare} />
					)}
					{tab === "gaps" && (
						<GapsList run={run} onSetFigure={handleSetFigure} />
					)}
					{tab === "sources" && (
						<SourcesList run={run} onOpenUri={handleOpenUri} />
					)}
					{tab === "history" && (
						<RunVersionsList
							versions={versions}
							loading={versionsLoading}
							error={versionsError}
							currentVersion={run.version}
						/>
					)}
				</div>
			</div>

			{/* The Analyst rail (DOP-450). Every turn carries this run. */}
			{analystEnabled && analystOpen && (
				<Suspense fallback={<RailFallback />}>
					<MonitoringAnalystRail
						contextLabel={monitoringChipLabel(run)}
						buildContext={() => buildMonitoringContext({ run, tab })}
						prefill={null}
						onClose={closeAnalyst}
						initialChatId={threadParam}
						onThreadChange={nameThread}
					/>
				</Suspense>
			)}

			{lockOpen && (
				<LockDialog
					run={run}
					onLock={(payload) => void handleLock(payload)}
					onClose={() => setLockOpen(false)}
					saving={locking}
				/>
			)}
		</div>
	);
};

const MonitoringRunPage: React.FC = () => (
	<PdfViewerProvider>
		<MonitoringRunView />
	</PdfViewerProvider>
);

export default MonitoringRunPage;
